import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { usersAPI } from '@/services/api';

const CompletarPerfil = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    // Endereço completo
    cep: '',
    endereco: '',
    numero: '',
    complemento: '',
    bairro: '',
    
    // Documentos
    cpf: '',
    rg: '',
    data_nascimento: '',
    
    // Informações bancárias (apenas para videomakers)
    banco: '',
    agencia: '',
    conta: '',
    tipo_conta: 'corrente',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCepChange = async (e) => {
    const cep = e.target.value.replace(/\D/g, '');
    setFormData({ ...formData, cep });
    
    if (cep.length === 8) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
        const data = await response.json();
        
        if (!data.erro) {
          setFormData({
            ...formData,
            cep,
            endereco: data.logradouro,
            bairro: data.bairro,
          });
          toast.success('CEP encontrado!');
        }
      } catch (error) {
        toast.error('Erro ao buscar CEP');
      }
    }
  };

  const formatCPF = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1');
  };

  const nextStep = () => {
    setStep(step + 1);
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      await usersAPI.updateProfile(formData);
      toast.success('Perfil completado com sucesso!');
      
      // Redirecionar baseado no role
      if (user.role === 'client') {
        navigate('/cliente/dashboard');
      } else if (user.role === 'videomaker') {
        navigate('/videomaker/dashboard');
      }
    } catch (error) {
      toast.error('Erro ao salvar informações');
    } finally {
      setLoading(false);
    }
  };

  const progress = (step / 2) * 100;

  return (
    <div className="min-h-full flex items-center justify-center py-12 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-2xl"
      >
        <Card className="shadow-2xl border-0 bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl">
          <CardHeader className="space-y-1">
            <CardTitle className="text-3xl font-bold bg-gradient-to-r from-[#0E76FF] to-[#2563EB] bg-clip-text text-transparent">
              Complete seu Perfil
            </CardTitle>
            <CardDescription className="text-gray-600 dark:text-gray-400">
              Apenas mais alguns dados para começar.
            </CardDescription>
            <Progress value={progress} className="mt-4" />
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center mt-2">
              Etapa {step} de 2
            </p>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Etapa 1: Endereço */}
            {step === 1 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  📍 Endereço Completo
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cep">CEP *</Label>
                    <Input
                      id="cep"
                      name="cep"
                      placeholder="00000-000"
                      value={formData.cep}
                      onChange={handleCepChange}
                      maxLength={9}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="numero">Número *</Label>
                    <Input
                      id="numero"
                      name="numero"
                      value={formData.numero}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="endereco">Endereço *</Label>
                  <Input
                    id="endereco"
                    name="endereco"
                    value={formData.endereco}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="complemento">Complemento</Label>
                    <Input
                      id="complemento"
                      name="complemento"
                      placeholder="Apto, bloco, etc"
                      value={formData.complemento}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bairro">Bairro *</Label>
                    <Input
                      id="bairro"
                      name="bairro"
                      value={formData.bairro}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <Button onClick={nextStep} className="w-full">
                  Próximo →
                </Button>
              </div>
            )}

            {/* Etapa 2: Documentos */}
            {step === 2 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  🆔 Documentos
                </h3>

                <div className="space-y-2">
                  <Label htmlFor="cpf">CPF *</Label>
                  <Input
                    id="cpf"
                    name="cpf"
                    placeholder="000.000.000-00"
                    value={formatCPF(formData.cpf)}
                    onChange={(e) => setFormData({ ...formData, cpf: e.target.value.replace(/\D/g, '') })}
                    maxLength={14}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="rg">RG</Label>
                  <Input
                    id="rg"
                    name="rg"
                    placeholder="00.000.000-0"
                    value={formData.rg}
                    onChange={handleChange}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="data_nascimento">Data de Nascimento *</Label>
                  <Input
                    id="data_nascimento"
                    name="data_nascimento"
                    type="date"
                    value={formData.data_nascimento}
                    onChange={handleChange}
                    required
                  />
                </div>

                {user?.role === 'videomaker' && (
                  <>
                    <Alert className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
                      <AlertDescription className="text-blue-800 dark:text-blue-200">
                        💰 Como videomaker, você precisa informar seus dados bancários para receber pagamentos
                      </AlertDescription>
                    </Alert>

                    <div className="space-y-2">
                      <Label htmlFor="banco">Banco *</Label>
                      <Input
                        id="banco"
                        name="banco"
                        placeholder="Ex: Banco do Brasil, Nubank, etc"
                        value={formData.banco}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="agencia">Agência *</Label>
                        <Input
                          id="agencia"
                          name="agencia"
                          placeholder="0000"
                          value={formData.agencia}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="conta">Conta *</Label>
                        <Input
                          id="conta"
                          name="conta"
                          placeholder="00000-0"
                          value={formData.conta}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>
                  </>
                )}

                <div className="flex gap-4">
                  <Button onClick={prevStep} variant="outline" className="w-full">
                    ← Voltar
                  </Button>
                  <Button onClick={handleSubmit} className="w-full" disabled={loading}>
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Salvando...
                      </div>
                    ) : (
                      '✓ Finalizar Cadastro'
                    )}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-gray-600 dark:text-gray-400 mt-4">
          🔒 Seus dados estão seguros conosco
        </p>
      </motion.div>
    </div>
  );
};

export default CompletarPerfil;
